import { stringify } from 'query-string';
import { isEqual } from 'lodash';
import { toUTC } from '../../lib/dates';

import * as _Types from '../../lib/jsdocTypedefs';
import * as _AvailabilityTypes from './types';
import { CALENDAR_CELL_STATES, SELECTED_DATES_STAY_STATUS } from './constants';
import { CAMPSITE_CALENDAR_TEXT } from '../../constants/campsiteStatus';
import availabilityTypes from '../../config/availabilityTypes';

export const checkIfEventIsClosedOnDate = (event, date) => {
  const dateTime = toUTC(date).getTime();
  if (event?.openDates?.length) {
    return !event.openDates.some((dataRange) => (
      dateTime >= toUTC(dataRange.fromDate).getTime()
        && dateTime <= toUTC(dataRange.toDate).getTime()
    ));
  }
  return false;
};

export const checkIfEventEndsOnDate = (event, date) => {
  const dateTime = date instanceof Date
    ? date.getTime() : date.toDate().getTime();

  if (event?.openDates?.length) {
    return event.openDates.some((dataRange) => (
      dateTime === new Date(dataRange.toDate).getTime()
    ));
  }

  return false;
};

export const checkIfEventIsClosedOnWithinDateRange = (event, start, end) => {
  if (start && end && event) {
    const startTime = start instanceof Date
      ? start.getTime() : new Date(start).getTime();

    const endTime = start instanceof Date
      ? end.getTime() : new Date(end).getTime();

    if (event?.openDates?.length) {
      return !event.openDates.some((dataRange) => (
        startTime >= new Date(dataRange.fromDate).getTime()
          && endTime <= new Date(dataRange.toDate).getTime()
      ));
    }
  }

  return false;
};

export const getIsOverseas = (router) => (
  Number(router.query?.isOverseas === 'true')
);

/**
 * Formats a party member to string for sending in a querystring
 * @param {_Types.PartyMember} partyMember
 * @param {number} index
 * @returns {string}
 */
const generatePartyMemberQueryString = (partyMember, index) => {
  const { personId, dateOfBirth, type } = partyMember;
  return `PartyMembers[${index}].PersonID=${personId}&PartyMembers[${index}].DateOfBirth=${dateOfBirth}&PartyMembers[${index}].Type=${type}`;
};

export const buildAmendmentAvailabilityPath = ({ args }) => {
  const { PartyMembers, ...stringArgs } = args;
  const stringifiedPersons = PartyMembers.map(generatePartyMemberQueryString).join('&');
  const querystring = `${stringify(stringArgs)}&${stringifiedPersons}`;
  return `BookingAmendment/BookingAmendmentAvailabilitySearch?${querystring}`;
};

/**
 * Deep-compares two membership arrays by types
 * @param {_Types.PartyMembers} members
 * @param {_Types.PartyMembers} newMembers
 * @returns {boolean} areEqual
 */
export const arePartyMemberArraysEqual = (members, newMembers) => {
  const membersTypes = members.map((member) => member.type).sort();
  const newMembersTypes = newMembers.map((member) => member.type).sort();
  return isEqual(membersTypes, newMembersTypes);
};

/**
 * Compares a quote and a booking to see if any key fields have changed
 * (e.g. party members, dates, etc.)
 * @param {_Types.Router} router
 * @param {_AvailabilityTypes.AvailabilitySearchVariables} variables
 * @param {_Types.BookingDetails} booking
 * @returns {boolean} hasChanged
 */
export const hasUpdatedQuoteFromBooking = (router, variables, booking) => {
  const { componentId } = router?.query ?? {};

  if (!booking) return false;

  const bookingCampsite = booking.campsiteBookings.find(
    (campsite) => campsite.id === componentId,
  );

  if (!bookingCampsite) return false;

  // Work out if any key price-dependent items have been changed
  // This currently just includes party members and dates
  const { partyMembers: selectedPartyMembers } = variables;

  const pitch = bookingCampsite.pitches[0];

  // We check if the party members list has changed
  const havePartyMembersChanged = !arePartyMemberArraysEqual(
    selectedPartyMembers,
    booking.partyMembers,
  );

  // Next we check if dates have been updated
  const { fromDate, toDate } = pitch.bookingDates;
  const isStartDateTheSame =
    variables.payload.start?.substring(0, 10) === fromDate?.substring(0, 10);
  const isEndDateTheSame =
    variables.payload.end?.substring(0, 10) === toDate?.substring(0, 10);
  const haveDatesChanged = !(isStartDateTheSame && isEndDateTheSame);

  return havePartyMembersChanged || haveDatesChanged;
};

/**
 * Generates neutral availability from the current pitches. This is essentially
 * a collation of all pitch availability.
 * @param {_Types.Pitch[]} pitches
 * @param {boolean} memberPricesAreSelected
 * @returns {_Types.Pitch}
 */
const generateNeutralAvailability = (
  pitches,
  memberPricesAreSelected = false,
) => {
  if (!pitches.length) return {};

  const pricesKey = memberPricesAreSelected
    ? 'memberAvailability'
    : 'nonMemberAvailability';

  if (!pitches[0][pricesKey]) return pitches[0];

  return new Array(pitches[0][pricesKey]?.length ?? 42)
    .fill(null)
    .map((_, index) => {
      // Returns each index's value in each pitch's availability
      const indexPitchAvailability = pitches.map((pitch) => (
        pitch[pricesKey]?.length ? pitch[pricesKey][index] : null));
      // Return true if any days have prices
      return indexPitchAvailability.some((price) => !!price);
    })
    .map((hasPrice) => (hasPrice
      ? CAMPSITE_CALENDAR_TEXT.AVAILABLE
      : CAMPSITE_CALENDAR_TEXT.SITE_FULL
    ));
};

/**
 * Generates low availability booleans for the neutral calendar
 * Essentially returns true for low if every pitch is low, false
 * otherwise
 * @param {_Types.Pitch[]} pitches
 * @returns {boolean[]}
 */
const getNeutralLowAvailability = (pitches) => {
  if (!pitches[0]?.hasLowAvailability?.length) return pitches[0]?.hasLowAvailability;
  // Generate an array which is the same size as another pitches' hasLowAvailability array.
  // Usually 42 days for a full calendar slide.
  return new Array(pitches[0].hasLowAvailability.length ?? 42)
    .fill(false)
    .map((_, index) => {
      // Returns an array of low availability bools across pitches
      // e.g. for 5 pitches we get [false, true, false, true, true]
      // It also removes any nulls for pitches that have no availability
      const indexPitchIsLow = pitches
        .map((pitch) => (pitch?.hasLowAvailability?.length
          ? pitch?.hasLowAvailability[index]
          : null
        ))
        .filter((value) => value !== null);
      // Return true if every available (i.e. non-null) pitch has low availability
      return indexPitchIsLow.every((price) => !!price);
    });
};

/**
 * Creates a 'fake' pitch object with the compiled availability
 * @param {_Types.Pitch[]} pitches
 * @returns {{
 *  memberAvailability: string[]  | number[],
 *  nonMemberAvailability: string[]  | number[],
 * }}
 */
export const generateNeutralCalendarPitch = (pitches) => {
  const memberAvailability = generateNeutralAvailability(pitches, true);
  const nonMemberAvailability = generateNeutralAvailability(pitches, false);
  const hasLowAvailability = getNeutralLowAvailability(pitches);
  return {
    memberAvailability,
    nonMemberAvailability,
    hasLowAvailability,
  };
};

/**
 * Generates an array full of PITCH_FULL pitch statuses
 * @param {number} length
 * @returns {string[]}
 */
const generateEmptyAvailability = (length) => new Array(length ?? 42).fill(
  CAMPSITE_CALENDAR_TEXT.PITCH_FULL,
);

/**
 * Maps any Neutral Calendar specific statuses which should show on pitch
 * specific calendars to those calendars, e.g. SITE_FULL.
 * @param {_Types.Pitch} selectedPitch
 * @param {_Types.Pitch} neutralAvailability
 * @returns {_Types.Pitch}
 */
export const mapNeutralAvailabilityToPitch = (selectedPitch, neutralAvailability) => {
  if (!selectedPitch) return selectedPitch;

  // Create a temporary pitch object which we will add the parsed fields to
  const parsedPitch = {
    ...selectedPitch,
  };

  if (
    !selectedPitch?.memberAvailability ||
    !selectedPitch?.nonMemberAvailability
  ) {
    // If there is no availability present, create an array full of 'PITCH_FULL' statuses
    const availabilityLength = neutralAvailability.memberAvailability?.length;
    parsedPitch.memberAvailability = generateEmptyAvailability(availabilityLength);
    parsedPitch.nonMemberAvailability = generateEmptyAvailability(availabilityLength);
  }

  // Replace any PITCH_FULL statuses with SITE_FULL where they are full for the whole
  // site.
  const memberAvailability = parsedPitch.memberAvailability.map(
    (availabilityItem, index) => {
      if (
        neutralAvailability.memberAvailability[index] ===
        CAMPSITE_CALENDAR_TEXT.SITE_FULL
      ) {
        return CAMPSITE_CALENDAR_TEXT.SITE_FULL;
      }
      return availabilityItem;
    },
  );
  const nonMemberAvailability = parsedPitch.nonMemberAvailability.map(
    (availabilityItem, index) => {
      if (
        neutralAvailability.nonMemberAvailability[index] ===
        CAMPSITE_CALENDAR_TEXT.SITE_FULL
      ) {
        return CAMPSITE_CALENDAR_TEXT.SITE_FULL;
      }
      return availabilityItem;
    },
  );

  return {
    ...parsedPitch,
    memberAvailability,
    nonMemberAvailability,
  };
};

/**
 * Determines if a user must book across pitches to book their requested dates, because
 * no singular pitch has availability for the duration.
 * @param {_Types.Pitch[]} pitches
 * @param {boolean} memberPricesAreSelected
 * @returns {number}
 */
export const mustBookAcrossPitches = (pitches, memberPricesAreSelected) => {
  if (!pitches?.length) return SELECTED_DATES_STAY_STATUS.NO_PITCHES_AVAILABLE;
  // first check if there's continuous availability on any pitch
  const availabilityKey = memberPricesAreSelected
    ? 'memberAvailability'
    : 'nonMemberAvailability';
  if (!pitches[0]?.[availabilityKey]) return SELECTED_DATES_STAY_STATUS.NO_PITCHES_AVAILABLE;
  const pitchesWithContinuousAvailability = pitches.map((pitch) => {
    if (!pitch && pitch?.[availabilityKey]) return false;
    const availability = pitch[availabilityKey];
    // Return true if every night has a defined price
    return availability?.every((night) => !!night);
  });
  const anyPitchHasContinuousAvailability =
    pitchesWithContinuousAvailability.some((value) => !!value);
  if (anyPitchHasContinuousAvailability) {
    return SELECTED_DATES_STAY_STATUS.CONTINUOUS_PITCH_AVAILABLE;
  }
  // Otherwise we need to go through every day and see if there's continuous
  // availability across any combination of pitches.
  const examplePitch = pitches.find((pitch) => !!pitch?.[availabilityKey]);
  const availabilitySize = examplePitch?.[availabilityKey]?.length;
  // If no pitches have per-night pricing present, then all are FULL
  if (!availabilitySize) return SELECTED_DATES_STAY_STATUS.NO_PITCHES_AVAILABLE;
  // Create a null-filled array for each night in the stay, which we replace
  // with booleans of whether there is a pitch with availability on that day
  // Minus one as we get a price back for each day (nights + 1)
  const availabilityPerNightAcrossPitches = new Array(availabilitySize)
    .fill(null)
    .map((_, index) => {
      const availabilityForThisNightAcrossPitches = pitches.map(
        (pitch) => pitch?.[availabilityKey]?.[index] ?? null,
      );
      // This returns an array of booleans for each pitch's availability on this night
      return availabilityForThisNightAcrossPitches.some((night) => !!night);
    }) ?? [];
  // This stores an array of booleans (one for each night) which are true if that night
  // is available on one or more pitches
  // So we can check if every item is true to see if there's a stay across pitches
  const someCombinationOfPitchesIsValid =
    availabilityPerNightAcrossPitches.every((night) => !!night);
  // Then we just check every night has a true value, meaning
  // there's some availability on any pitches for the duration
  return someCombinationOfPitchesIsValid
    ? SELECTED_DATES_STAY_STATUS.MUST_BOOK_ACROSS_PITCHES
    : SELECTED_DATES_STAY_STATUS.NO_PITCHES_AVAILABLE;
};

/**
 * Returns a boolean denoting if a site is full for every day provided
 * @param {_Types.Pitch[]} pitches
 * @param {boolean} memberPricesAreSelected
 * @returns {boolean}
 */
export const isSiteFull = (pitches, memberPricesAreSelected) => {
  const availabilityKey = memberPricesAreSelected
    ? availabilityTypes.MEMBER
    : availabilityTypes.NON_MEMBER;
  if (!pitches) return true;
  const pitchWithAvailability = pitches.find((pitch) => !!pitch?.[availabilityKey]?.length);
  // If no pitches have availability arrays, then the site is full
  if (!pitchWithAvailability) return true;
  const selectedNumberOfDays = pitchWithAvailability?.[availabilityKey]?.length ?? 0;
  if (selectedNumberOfDays === 0) return false;
  // BE returns all days selected, including the departure day which is valid if it's FULL
  // so we remove that here to make sure we only flag FULL days on which users are
  // staying the night.
  const isSiteFullDuringStay = new Array(selectedNumberOfDays - 1)
    .fill(null).map((_, dayNumber) => {
      const isAPitchAvailableForThisDay = pitches.map((pitch) => {
        const availability = pitch?.[availabilityKey];
        if (!availability) return null;
        return availability[dayNumber];
      }).some((value) => !!value);
      return isAPitchAvailableForThisDay;
    }).some((value) => !value);
  return isSiteFullDuringStay;
};

/**
 * Determines if the pitch or site is full from an AvailabilityTotal request
 * (AvailabilitySearch with dates)
 * @param {_Types.Pitch[]} availabilityTotal
 * @param {_Types.Pitch} selectedPitch
 * @param {boolean} memberPricesAreSelected
 * @returns {{
 *  pitchFull: boolean,
 *  siteFull: boolean,
 *  areSomePitchesFull: boolean
 * }}
 */
export const parseAvailabilityTotalErrors = (
  availabilityTotalPitchTypes,
  selectedPitch,
  memberPricesAreSelected,
) => {
  const totalPriceKey = memberPricesAreSelected
    ? availabilityTypes.MEMBER_TOTAL
    : availabilityTypes.NON_MEMBER_TOTAL;
  const isNeutralCalendar = !selectedPitch?.id;
  const chosenPitch = availabilityTotalPitchTypes.find(
    (pitch) => pitch?.code === selectedPitch?.id,
  );
  // This pitch is full if it doesn't return a total price
  const pitchHasAvailability = chosenPitch?.[totalPriceKey];
  const isThisPitchFull = !pitchHasAvailability;

  // Here we go through each day of the selected dates and check if pricing
  // is null for every date, if so, the site is full
  const siteFull = isSiteFull(availabilityTotalPitchTypes, memberPricesAreSelected);

  // A pitch is full if we don't get a total price back for it
  const areSomePitchesFull = availabilityTotalPitchTypes
    .map((pitch) => !!pitch?.[totalPriceKey])
    .some((hasAvailability) => !hasAvailability);

  const siteHasDaysOnRequest = availabilityTotalPitchTypes
    .map((pitch) => pitch.daysOnRequest?.length > 0)
    .some((pitchIsOnRequest) => pitchIsOnRequest);

  const { availabilityTotalPitchData: selectedAvailabilityTotal } = selectedPitch;
  const pitchHasDaysOnRequest = selectedAvailabilityTotal?.daysOnRequest?.length > 0;

  return {
    pitchFull: !isNeutralCalendar && isThisPitchFull,
    siteFull,
    areSomePitchesFull,
    siteHasDaysOnRequest,
    pitchHasDaysOnRequest,
  };
};

/**
 * Checks if a string is a price via Regex
 * @param {string} price
 * @returns {boolean}
 */
export const isPrice = (price) => {
  const regex = new RegExp('^£?\\d{1,3}(,\\d{3})*(\\.\\d{2})?$');
  return regex.test(price);
};

/**
 * A centralized helper for generating calendar cell state from a group
 * of variables.
 * @returns {{
 *  variant: string,
 *  text: string,
 * }}
 */
export const calculateCellState = ({
  isNeutralCalendar,
  isClosed,
  isSelected,
  cellText,
  isInThePast,
  isEndDate,
  isSelectedEndDate,
  isSearchFormPicker,
  isBooked,
  isLoading,
  isSiteFull: isChosenSiteFull,
  isOnRequest,
  disabled,
  hasValidDates,
}) => {
  const cellStates = isNeutralCalendar
    ? CALENDAR_CELL_STATES.NEUTRAL
    : CALENDAR_CELL_STATES.PITCH;

  const isFull = !cellText
    || cellText === CAMPSITE_CALENDAR_TEXT.SITE_FULL
    || cellText === CAMPSITE_CALENDAR_TEXT.PITCH_FULL;

  if (isInThePast || disabled) {
    return {
      variant: cellStates.DISABLED,
      text: '',
    };
  }

  if (isSearchFormPicker && (isSelected || isSelectedEndDate)) {
    return {
      variant: cellStates.SELECTED,
      text: '',
    };
  }

  if (isSearchFormPicker) {
    return {
      variant: cellStates.PLAIN,
      text: '',
    };
  }

  if (isClosed) {
    return {
      variant: cellStates.CONTACT_US,
      text: CAMPSITE_CALENDAR_TEXT.CLOSED,
    };
  }

  if (isOnRequest && isSelected) {
    return {
      variant: cellStates.SELECTED_CONTACT_US,
      text: cellText,
    };
  }

  if (isOnRequest) {
    return {
      variant: cellStates.CONTACT_US,
      text: cellText,
    };
  }

  if (!isLoading && isSelected && isFull) {
    return {
      variant: cellStates.SELECTED_FULL,
      text: isChosenSiteFull
        ? CAMPSITE_CALENDAR_TEXT.SITE_FULL
        : CAMPSITE_CALENDAR_TEXT.PITCH_FULL,
    };
  }

  if (!isLoading && !isSelected && isSelectedEndDate) {
    return {
      variant: cellStates.BOOKED_UNSELECTED_LAST,
      text: isNeutralCalendar ? '' : cellText,
    };
  }

  if (hasValidDates && isEndDate) {
    return {
      variant: cellStates.SELECTED_END,
      text: (isNeutralCalendar || isLoading) ? '' : cellText,
    };
  }

  if (isSelected) {
    return {
      variant: cellStates.SELECTED,
      text: (isNeutralCalendar || isLoading) ? '' : cellText,
    };
  }

  if (isBooked) {
    return {
      variant: cellStates.BOOKED_UNSELECTED,
      text: isNeutralCalendar ? '' : cellText,
    };
  }

  if (isLoading) {
    return {
      variant: cellStates.AVAILABLE,
      text: '',
    };
  }

  if (cellText === CAMPSITE_CALENDAR_TEXT.AVAILABLE || isPrice(cellText)) {
    return {
      variant: cellStates.AVAILABLE,
      text: isNeutralCalendar ? '' : cellText,
    };
  }

  if (cellText === CAMPSITE_CALENDAR_TEXT.SITE_FULL || isChosenSiteFull) {
    // This cell can show on both the neutral and pitch-specific calendars
    return {
      variant: CALENDAR_CELL_STATES.NEUTRAL.FULL,
      text: CAMPSITE_CALENDAR_TEXT.SITE_FULL,
    };
  }

  // Default to returning the deselected FULL styling
  return {
    variant: cellStates.FULL,
    text: CAMPSITE_CALENDAR_TEXT.PITCH_FULL,
  };
};

/**
 * Returns values for which a key is at it's maximum or minimum (extrema). Whether
 * a max or min is returned is defined by the `operator` parameter
 * @param {_Types.PitchWithTotalPrice[]} pitches
 * @param {string} key
 * @param {'>' | '<'} operator
 * @returns {_Types.PitchWithTotalPrice[]}
 */
const getExtremaPitchValues = (pitches, key, operator = '>') => {
  const filteredPitches = pitches.filter((pitch) => !!pitch[key]);
  if (!filteredPitches.length) return [pitches[0]];
  const extremaValue = filteredPitches.reduce(
    (prev, curr) => {
      const result = (operator === '>' ? (prev && prev?.[key] > curr?.[key]) : (prev && prev?.[key] < curr?.[key]));
      return result ? curr : prev;
    },
  );
  return filteredPitches.filter((pitch) => pitch?.[key] === extremaValue?.[key]);
};

/**
 * Returns a default pitch first by sort order, then by min price per night, then
 * by name alphabetically in the event of duplicate values.
 * @param {_Types.PitchWithTotalPrice[]} pitches
 * @param {boolean} memberPricesAreSelected
 * @returns {_Types.PitchWithTotalPrice}
 */
const getPitchByTotalPriceSortOrderAndMinPrice = (pitches, memberPricesAreSelected) => {
  const pricePerNightKey = memberPricesAreSelected ? 'memberMinPricePerNight' : 'nonMemberMinPricePerNight';
  // First check if all pitches have a sort order of 0, if so, skip this step
  const nonZeroPriorityPitches = pitches.filter(
    ({ prioritySortOrder } = { prioritySortOrder: 0 }) => prioritySortOrder !== 0,
  );
  const allPitchesArePriorityZero = nonZeroPriorityPitches.length === 0;
  const pitchesWithHighestSortOrder = allPitchesArePriorityZero
    ? pitches
    : getExtremaPitchValues(nonZeroPriorityPitches, 'prioritySortOrder', '>');
  if (pitchesWithHighestSortOrder.length === 1) return pitchesWithHighestSortOrder[0];
  // If we have multiple pitches with the same total price & sort order, now get the lowest
  // minimum price per night
  const pitchesWithMinNightlyPrice = getExtremaPitchValues(pitchesWithHighestSortOrder, pricePerNightKey, '>');
  if (pitchesWithMinNightlyPrice.length === 1) return pitchesWithMinNightlyPrice[0];
  // Finally return first alphabetically if everything else is identical
  return pitchesWithMinNightlyPrice.sort(
    (pitchA, pitchB) => pitchA.name.localeCompare(pitchB.name),
  )[0];
};

/**
 * Picks a default pitch when loading into SC3
 * @param {_Types.PitchWithTotalPrice[]} pitches
 * @param {boolean} hasDates
 * @returns {_Types.PitchWithTotalPrice}
 */
export const getDefaultPitch = (pitches, hasDates, memberPricesAreSelected) => {
  if (!pitches || !pitches?.length) return null;
  const pricePerNightKey = memberPricesAreSelected ? 'memberMinPricePerNight' : 'nonMemberMinPricePerNight';
  if (hasDates) {
    const totalPriceKey = memberPricesAreSelected ? 'memberAvailabilityTotalPrice' : 'nonMemberAvailabilityTotalPrice';
    const filteredPitches = pitches.filter((pitch) => !!pitch[totalPriceKey]);
    if (!filteredPitches.length) return pitches[0];
    // If we have dates, pick the pitch with the lowest total stay cost
    const pitchesWithMinTotalPrice = getExtremaPitchValues(filteredPitches, totalPriceKey, '>');
    if (pitchesWithMinTotalPrice.length === 1) return pitchesWithMinTotalPrice[0];
    // If we have multiple pitches with the same total price, now sort on sort order
    return getPitchByTotalPriceSortOrderAndMinPrice(
      pitchesWithMinTotalPrice,
      memberPricesAreSelected,
    );
  }
  // If we don't have dates, remove any unavailable pitches from being selected
  const filteredPitches = pitches.filter((pitch) => !!pitch[pricePerNightKey]);
  // If no pitches have a price return the first
  if (!filteredPitches.length) return pitches[0];
  return getPitchByTotalPriceSortOrderAndMinPrice(filteredPitches, memberPricesAreSelected);
};
